import { useCallback, useEffect, useState } from "react";
import {
  Link2, Loader2, MessageSquareWarning, Search, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { dateTime, hashShort } from "@/lib/format";

const lastSevenDays = () => ({
  from: isoDay(new Date(Date.now() - 6 * 86400000)),
  to: isoDay(new Date()),
});

const dayOf = (value) => {
  if (!value || value.length < 10) return undefined;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const TRIGGER_KEYS = ["triggered_by", "initiated_by", "requested_by", "raised_by", "sent_by",
                      "prepared_by", "posted_by", "actor", "user", "user_email", "staff_email", "by"];

/** Only an entry with a person behind it can be queried. */
const askable = (event) => {
  const actor = (event.actor_email || "").trim();
  if (actor && actor !== "system") return true;
  return TRIGGER_KEYS.some((k) => typeof event.details?.[k] === "string"
    && event.details[k].includes("@"));
};

export default function AuditExplorer() {
  const [events, setEvents] = useState(null);
  const detail = useRowDetail();
  const [chain, setChain] = useState(null);
  const [q, setQ] = useState("");
  const [range, setRange] = useState(lastSevenDays);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState({ query_type: "information", question: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (action, window) => {
    const params = new URLSearchParams({ limit: "300" });
    if (action) params.set("action", action);
    if (window?.from) params.set("date_from", window.from);
    if (window?.to) params.set("date_to", window.to);
    const { data } = await api.get(`/regulatory/audit?${params.toString()}`);
    setEvents(data);
  }, []);

  useEffect(() => { load("", lastSevenDays()).catch(() => setEvents([])); }, [load]);

  const raiseQuery = async () => {
    setBusy(true);
    try {
      const res = await api.post("/inquiries", {
        audit_seq: selected.seq, query_type: query.query_type, question: query.question,
      });
      toast.success(`${res.data.inquiry_ref} sent to ${res.data.respondent}`);
      setSelected(null);
      setQuery({ query_type: "information", question: "" });
      await load(q, range);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy(false);
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/regulatory/audit/verify");
      setChain(data);
      toast[data.valid ? "success" : "error"](
        data.valid ? `Hash chain intact across ${data.events_checked} events`
          : `Chain broken at sequence ${data.broken_at_seq}`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };
  if (!events) return <Loading label="Reading immutable audit log" />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-015 · Audit Explorer"
        title="Reconstruct exactly why a past decision was made"
        description="Every privileged action, rule publication and regulatory decision is appended to a SHA-256 hash chain. Each event binds to its predecessor, so any retro-edit breaks the chain."
        actions={
          <Button onClick={verify} disabled={busy} data-testid="verify-chain-button"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />}
            Verify hash chain
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-audit-events" label="Events in view" value={events.length} accent="sky" />
        <Kpi testId="kpi-audit-head" label="Head sequence" accent="violet"
             value={events.length ? events[0].seq : 0} />
        <Kpi testId="kpi-audit-chain" label="Chain integrity" accent={chain ? (chain.valid ? "emerald" : "red") : "slate"}
             value={chain ? (chain.valid ? "INTACT" : "BROKEN") : "unverified"}
             sub={chain ? `${chain.events_checked} events checked` : "Run verification"} />
      </div>

      {chain && (
        <div className={`flex items-center gap-2 rounded border p-3 text-xs ${chain.valid
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
          : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}`} data-testid="chain-verify-banner">
          {chain.valid ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          {chain.valid
            ? <span>Tamper-evident chain verified. Head hash <span className="font-mono">{hashShort(chain.head_hash)}</span></span>
            : <span>Chain integrity failure at sequence {chain.broken_at_seq}.</span>}
        </div>
      )}

      <Panel
        testId="panel-audit"
        kicker="Append-only"
        title="Audit events"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter action e.g. rule."
                     data-testid="audit-filter-input" className="h-8 w-44 bg-muted/40 pl-8 text-xs" />
            </div>
            <DateField value={range.from} testId="audit-from-input" placeholder="From"
                       disabled={{ after: dayOf(range.to) || new Date() }}
                       onChange={(v) => setRange({ ...range, from: v })}
                       className="h-8 w-[150px] text-[11.5px]" />
            <DateField value={range.to} testId="audit-to-input" placeholder="To"
                       disabled={{ before: dayOf(range.from), after: new Date() }}
                       onChange={(v) => setRange({ ...range, to: v })}
                       className="h-8 w-[150px] text-[11.5px]" />
            <Button size="sm" variant="outline" onClick={() => load(q, range)} data-testid="audit-filter-button"
                    className="h-8 rounded-full border-border text-xs">Apply</Button>
            <Button size="sm" variant="ghost" data-testid="audit-reset-button"
                    onClick={() => {
                      const window7 = lastSevenDays();
                      setQ(""); setRange(window7); load("", window7);
                    }}
                    className="h-8 rounded-full text-xs text-muted-foreground">Last 7 days</Button>
          </div>
        }
      >
        <div className="max-h-[620px] overflow-y-auto">
          <Table testId="audit-table"
                 head={<><Th>Seq</Th><Th>When</Th><Th>What happened</Th><Th>Actor</Th>
                   <Th>Warnings</Th><Th>Query</Th><Th>Hash</Th></>}>
            {events.length === 0 && <Empty colSpan={7} label="No audit events in this window" />}
            {events.map((e) => (
              <tr key={e.seq} className="row-hover cursor-pointer"
                  onClick={rowClick(() => setSelected(e))} data-testid={`audit-row-${e.seq}`}>
                <Td className="font-mono text-[11px] text-muted-foreground">{String(e.seq).padStart(5, "0")}</Td>
                <Td className="whitespace-nowrap font-mono text-[11px]">{dateTime(e.occurred_at)}</Td>
                <Td className="max-w-[420px]">
                  <div className="text-[12px] text-foreground">{e.summary}</div>
                  <div className="font-mono text-[10px] text-muted-foreground/70">{e.action}</div>
                </Td>
                <Td>
                  <div className="text-[11px] text-foreground/80">{e.actor_email}</div>
                  <div className="font-mono text-[10px] text-muted-foreground/70">{e.actor_role}</div>
                </Td>
                <Td>
                  {(e.warnings || []).length === 0
                    ? <span className="text-[10.5px] text-muted-foreground">—</span>
                    : <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                        {e.warnings.length} flag{e.warnings.length > 1 ? "s" : ""}
                      </Chip>}
                </Td>
                <Td>
                  {e.inquiry
                    ? <Chip className="border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300">
                        {e.inquiry.inquiry_ref}
                      </Chip>
                    : <span className="text-[10.5px] text-muted-foreground">—</span>}
                </Td>
                <Td className="font-mono text-[10px] text-muted-foreground/70" data-testid={`audit-hash-${e.seq}`}>
                  {hashShort(e.hash)}
                </Td>
              </tr>
            ))}
          </Table>
        </div>
      </Panel>

      <Sheet open={Boolean(selected)} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl"
                      data-testid="audit-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">
                  Event {String(selected.seq).padStart(5, "0")}
                </SheetTitle>
              </SheetHeader>
              <p className="mt-3 text-[13px] text-foreground">{selected.summary}</p>
              <div className="mt-4 space-y-1.5 text-[12px]">
                {[["Recorded at", dateTime(selected.occurred_at)],
                  ["Action", selected.action],
                  ["Actor", `${selected.actor_email} (${selected.actor_role})`],
                  ["Subject", `${selected.resource} ${selected.resource_id || ""}`],
                  ["Tenant", selected.tenant_id]].map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5">
                      <span className="text-[11px] text-muted-foreground">{k}</span>
                      <span className="text-right text-foreground">{v || "—"}</span>
                    </div>
                ))}
                {Object.entries(selected.details || {}).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5">
                    <span className="text-[11px] text-muted-foreground">{k.replace(/_/g, " ")}</span>
                    <span className="text-right font-mono text-[11px] text-foreground">
                      {Array.isArray(v) ? v.join(", ") : String(v)}
                    </span>
                  </div>
                ))}
              </div>

              {(selected.warnings || []).length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3"
                     data-testid="audit-detail-warnings">
                  {selected.warnings.map((w) => (
                    <div key={w} className="flex items-start gap-2 text-[11.5px] text-amber-700 dark:text-amber-300">
                      <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 space-y-1 rounded-xl border border-border bg-muted/20 p-3 font-mono text-[10.5px] text-muted-foreground">
                <div>hash {selected.hash}</div>
                <div>prev {selected.prev_hash}</div>
              </div>

              {selected.inquiry ? (
                <div className="mt-4 rounded-xl border border-violet-500/30 bg-violet-500/10 p-3 text-[11.5px] text-violet-700 dark:text-violet-300"
                     data-testid="audit-detail-inquiry">
                  Query {selected.inquiry.inquiry_ref} is open on this entry — {selected.inquiry.remark}
                </div>
              ) : !askable(selected) ? (
                <div className="mt-4 rounded-xl border border-border bg-muted/30 p-3 text-[11.5px] text-muted-foreground"
                     data-testid="audit-detail-system">
                  The platform recorded this entry itself and no person triggered it, so there is
                  nobody to ask. Raise it with the Board Secretary if it needs explaining.
                </div>
              ) : (
                <div className="mt-5 space-y-3 border-t border-border pt-4">
                  <div className="kicker">Raise a query</div>
                  <p className="text-[11.5px] text-muted-foreground">
                    This asks {selected.actor_email} to explain what they did. The thread appears
                    under Governance → Inquiries.
                  </p>
                  <Select value={query.query_type}
                          onValueChange={(v) => setQuery({ ...query, query_type: v })}>
                    <SelectTrigger data-testid="audit-query-type" className="bg-muted/40">
                      <SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="information">Information</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="inquiry">Inquiry</SelectItem>
                      <SelectItem value="disciplinary">Disciplinary action</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea rows={4} value={query.question} data-testid="audit-query-question"
                            onChange={(e) => setQuery({ ...query, question: e.target.value })}
                            placeholder="What would you like them to explain?"
                            className="bg-muted/40 text-[12.5px]" />
                  <Button onClick={raiseQuery} disabled={busy || !query.question.trim()}
                          data-testid="audit-raise-query"
                          className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <MessageSquareWarning className="mr-2 h-4 w-4" />} Raise query
                  </Button>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {detail.sheet}
    </div>
  );
}
